import { CheckCircleFilled } from "@ant-design/icons"
import { Button, Checkbox, Form, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { RoutesPaths } from "../../../../../constants/routes.ts"
import { useDateFormat } from "../../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../../utils/date/date.ts"
import { getLeadEducation } from "../../../../Lead/LeadWorkspacePage/education/api/education.services.ts"
import { INTERNAL_READABLE_STATUSES } from "../../../../Lead/LeadWorkspacePage/education/education-contract.ts"
import type { LeadEducationView } from "../../../../Lead/LeadWorkspacePage/education/types/education.types.ts"
import type { Event } from "../../../types/types.ts"

export type ProjectEducationAudience = "sales_person" | "customer"

type LoadState = "loading" | "loaded" | "failed"

interface ProjectEducationState {
  salesBriefReady: boolean
  customerGuidePublished: boolean
  customerGuidePublishedAt: string | null
  canShareGuide: boolean
}

// Mirrors ezbuild_back `invitations.customer_guide_published`: any published,
// staff-authored edition in the active package counts, not only the newest.
const deriveFromLeadEducation = (data: LeadEducationView): ProjectEducationState => {
  const tracks = data.package?.tracks ?? []
  const salesTrack = tracks.find((track) => track.audience === "sales_person")
  const customerTrack = tracks.find((track) => track.audience === "customer")
  const publishedCustomerEditions = (customerTrack?.editions ?? []).filter(
    (edition) => edition.status === "published" && edition.created_by.type !== "customer"
  )
  const latestPublished = publishedCustomerEditions[publishedCustomerEditions.length - 1]

  return {
    salesBriefReady: !!salesTrack?.editions.some((edition) =>
      INTERNAL_READABLE_STATUSES.includes(edition.status)
    ),
    customerGuidePublished: publishedCustomerEditions.length > 0,
    customerGuidePublishedAt: latestPublished?.published_at ?? null,
    canShareGuide: data.permissions.can_generate
  }
}

// An older backend sends no `can_share_guide`; it keeps offering the checkbox
// the way it did before the permission existed.
const deriveFromEvent = (
  projectEducation: Event["project_education"] | undefined
): ProjectEducationState => ({
  salesBriefReady: !!projectEducation?.sales_brief_ready,
  customerGuidePublished: !!projectEducation?.customer_guide_published,
  customerGuidePublishedAt: null,
  canShareGuide: projectEducation?.can_share_guide ?? true
})

const openLeadEducationTab = (leadId: string, audience: ProjectEducationAudience) => {
  const url = `${RoutesPaths.lead.view(leadId, { tab: "education" })}&audience=${audience}`
  window.open(url, "_blank", "noopener,noreferrer")
}

interface ProjectEducationInviteSectionProps {
  leadId: string
  isEdit: boolean
  inviteCustomer: boolean
  /** `feature_project_education_customer_sharing` on `/me`. */
  showCustomerSharing: boolean
  eventProjectEducation?: Event["project_education"]
}

/**
 * The meeting composer's "Project education" block. The Sales Person Brief
 * row is a statement, not a control: the backend always sends it to the
 * Internal Participants (Team). The share checkbox is the one form field,
 * `share_project_education`.
 */
export function ProjectEducationInviteSection({
  leadId,
  isEdit,
  inviteCustomer,
  showCustomerSharing,
  eventProjectEducation
}: ProjectEducationInviteSectionProps) {
  const { t } = useTranslation()
  const { userFormat } = useDateFormat()
  const [fetched, setFetched] = useState<{ leadId: string; data: LeadEducationView } | null>(null)
  const [loadState, setLoadState] = useState<LoadState>("loading")

  useEffect(() => {
    if (isEdit || !leadId) return

    let cancelled = false
    setLoadState("loading")
    void getLeadEducation(leadId).then((response) => {
      if (cancelled) return
      if (response.ok && response.data) {
        setFetched({ leadId, data: response.data })
        setLoadState("loaded")
      } else {
        setLoadState("failed")
      }
    })

    return () => {
      cancelled = true
    }
  }, [isEdit, leadId])

  const createModeData = fetched?.leadId === leadId ? fetched.data : null
  const statusKnown = isEdit || (loadState === "loaded" && !!createModeData)
  const state: ProjectEducationState | null = isEdit
    ? deriveFromEvent(eventProjectEducation)
    : statusKnown && createModeData
      ? deriveFromLeadEducation(createModeData)
      : null

  const renderBriefStatus = () => {
    if (!state) {
      return (
        <Tag>
          {loadState === "failed"
            ? t("projectEducation.inviteSection.statusUnavailable")
            : t("projectEducation.inviteSection.statusLoading")}
        </Tag>
      )
    }
    if (state.salesBriefReady) {
      return <Tag color={"green"}>{t("projectEducation.inviteSection.included")}</Tag>
    }
    return (
      <>
        <Tag color={"orange"}>{t("projectEducation.inviteSection.notGenerated")}</Tag>
        <Button
          type={"link"}
          size={"small"}
          className={"px-0"}
          onClick={() => openLeadEducationTab(leadId, "sales_person")}
        >
          {t("projectEducation.inviteSection.generate")}
        </Button>
      </>
    )
  }

  const showShareCheckbox = inviteCustomer && showCustomerSharing && !!state?.canShareGuide

  return (
    <div className={"flex flex-col gap-3"}>
      <Typography.Text
        strong
        className={"block text-xs uppercase tracking-wide text-gray-500"}
      >
        {t("projectEducation.inviteSection.heading")}
      </Typography.Text>

      <div className={"min-w-0"} data-testid={"project-education-sales-brief"}>
        <CheckCircleFilled
          className={state?.salesBriefReady ? "text-green-600" : "text-gray-400"}
          aria-hidden
        />{" "}
        <span className={"font-medium"}>{t("projectEducation.inviteSection.salesBrief")}</span>{" "}
        {renderBriefStatus()}
        <Typography.Text type={"secondary"} className={"mt-1 block pl-6 text-xs"}>
          {t("projectEducation.inviteSection.privateNote")}{" "}
          {state?.salesBriefReady && (
            <Button
              type={"link"}
              size={"small"}
              className={"px-0"}
              onClick={() => openLeadEducationTab(leadId, "sales_person")}
            >
              {t("projectEducation.inviteSection.preview")}
            </Button>
          )}
        </Typography.Text>
        {state && !state.salesBriefReady && (
          <Typography.Text type={"secondary"} className={"mt-1 block pl-6 text-xs"}>
            {t("projectEducation.inviteSection.sentWhenGenerated")}
          </Typography.Text>
        )}
      </div>

      {showShareCheckbox && state && (
        <div className={"min-w-0"}>
          <Form.Item
            name="share_project_education"
            valuePropName="checked"
            className={"mb-0 min-w-0"}
          >
            <Checkbox>
              <span className={"font-medium"}>{t("projectEducation.inviteSection.shareLabel")}</span>
            </Checkbox>
          </Form.Item>
          <Typography.Text type={"secondary"} className={"mt-1 block pl-6 text-xs"}>
            {t("projectEducation.inviteSection.shareHelp")}
          </Typography.Text>
          {state.customerGuidePublished ? (
            <Typography.Text type={"secondary"} className={"mt-1 block pl-6 text-xs"}>
              {state.customerGuidePublishedAt
                ? t("projectEducation.inviteSection.customerGuidePublishedOn", {
                    date: dayjs(state.customerGuidePublishedAt).format(userFormat || DATE_FORMAT)
                  })
                : t("projectEducation.inviteSection.customerGuidePublished")}{" "}
              <Button
                type={"link"}
                size={"small"}
                className={"px-0"}
                onClick={() => openLeadEducationTab(leadId, "customer")}
              >
                {t("projectEducation.inviteSection.preview")}
              </Button>
            </Typography.Text>
          ) : (
            <Typography.Text type={"secondary"} className={"mt-1 block pl-6 text-xs"}>
              {t("projectEducation.inviteSection.customerGuideAutomatic")}
            </Typography.Text>
          )}
        </div>
      )}
    </div>
  )
}
